'use client';

/**
 * Explore Filter Context
 *
 * Provides shared state for explore filters across the sidebar and content pages.
 * When users click filter buttons in the sidebar, the selected filters are applied
 * to character listings throughout the app.
 */
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useState,
} from 'react';

export type FilterCategory =
  | 'Female'
  | 'Male'
  | 'Realistic'
  | 'Anime'
  | 'Furry'
  | 'Busty'
  | 'Petite'
  | 'Athletic'
  | 'Dominant'
  | 'Submissive'
  | 'Softspoken'
  | 'Outgoing'
  | '18-21'
  | '22-30'
  | '30-35'
  | '35+'
  | 'Romance'
  | 'Roleplay'
  | 'Kissing'
  | 'Foreplay'
  | 'Smut'
  | 'JOI'
  | 'Action'
  | 'Comedy'
  | 'Fantasy'
  | 'BDSM'
  | 'Cheating'
  | 'Magical'
  | 'Breeding'
  | 'Bisexual'
  | 'Heterosexual'
  | 'Pansexual';

// Map filter categories to database fields
export const FILTER_FIELD_MAP: Record<
  string,
  { field: string; value?: string }
> = {
  // Gender filters
  Female: { field: 'gender', value: 'female' },
  Male: { field: 'gender', value: 'male' },

  // Style filters
  Realistic: { field: 'style', value: 'realistic' },
  Anime: { field: 'style', value: 'anime' },
  Furry: { field: 'style', value: 'furry' },

  // Body type filters
  Busty: { field: 'body_type', value: 'voluptuous' },
  Petite: { field: 'body_type', value: 'petite' },
  Athletic: { field: 'body_type', value: 'athletic' },

  // Age range filters (mapped to age ranges)
  '18-21': { field: 'age_range', value: '18-21' },
  '22-30': { field: 'age_range', value: '22-30' },
  '30-35': { field: 'age_range', value: '30-35' },
  '35+': { field: 'age_range', value: '35+' },

  // Other filters map to tags
  Dominant: { field: 'tags' },
  Submissive: { field: 'tags' },
  Softspoken: { field: 'tags' },
  Outgoing: { field: 'tags' },
  Romance: { field: 'tags' },
  Roleplay: { field: 'tags' },
  Kissing: { field: 'tags' },
  Foreplay: { field: 'tags' },
  Smut: { field: 'tags' },
  JOI: { field: 'tags' },
  Action: { field: 'tags' },
  Comedy: { field: 'tags' },
  Fantasy: { field: 'tags' },
  BDSM: { field: 'tags' },
  Cheating: { field: 'tags' },
  Magical: { field: 'tags' },
  Breeding: { field: 'tags' },
  Bisexual: { field: 'tags' },
  Heterosexual: { field: 'tags' },
  Pansexual: { field: 'tags' },
};

interface ExploreFilterContextType {
  selectedFilters: string[];
  toggleFilter: (filter: string) => void;
  clearFilters: () => void;
  setFilters: (filters: string[]) => void;
  isFilterSelected: (filter: string) => boolean;
  // Parsed filters for querying
  genderFilter: string | null;
  styleFilter: string | null;
  bodyTypeFilter: string | null;
  ageRangeFilter: string | null;
  tagFilters: string[];
}

const ExploreFilterContext = createContext<
  ExploreFilterContextType | undefined
>(undefined);

export function ExploreFilterProvider({ children }: { children: ReactNode }) {
  const [selectedFilters, setSelectedFilters] = useState<string[]>([
    'Female',
    'Realistic',
  ]);

  const toggleFilter = useCallback((filter: string) => {
    setSelectedFilters((prev) =>
      prev.includes(filter)
        ? prev.filter((f) => f !== filter)
        : [...prev, filter],
    );
  }, []);

  const clearFilters = useCallback(() => {
    setSelectedFilters([]);
  }, []);

  const setFilters = useCallback((filters: string[]) => {
    setSelectedFilters(filters);
  }, []);

  const isFilterSelected = useCallback(
    (filter: string) => {
      return selectedFilters.includes(filter);
    },
    [selectedFilters],
  );

  // Parse selected filters into query-friendly format
  const genderFilter = selectedFilters.find(
    (f) => f === 'Female' || f === 'Male',
  )
    ? selectedFilters.includes('Female') && !selectedFilters.includes('Male')
      ? 'female'
      : selectedFilters.includes('Male') && !selectedFilters.includes('Female')
        ? 'male'
        : null // Both or neither = no filter
    : null;

  const styleFilters = selectedFilters.filter((f) =>
    ['Realistic', 'Anime', 'Furry'].includes(f),
  );
  const styleFilter =
    styleFilters.length === 1
      ? (FILTER_FIELD_MAP[styleFilters[0]!]?.value ?? null)
      : null;

  const bodyTypeFilter = selectedFilters.find((f) =>
    ['Busty', 'Petite', 'Athletic'].includes(f),
  )
    ? FILTER_FIELD_MAP[
        selectedFilters.find((f) =>
          ['Busty', 'Petite', 'Athletic'].includes(f),
        )!
      ]?.value || null
    : null;

  const ageRangeFilter = selectedFilters.find((f) =>
    ['18-21', '22-30', '30-35', '35+'].includes(f),
  )
    ? selectedFilters.find((f) =>
        ['18-21', '22-30', '30-35', '35+'].includes(f),
      ) || null
    : null;

  // Tags are everything else that maps to 'tags' field
  const tagFilters = selectedFilters.filter((f) => {
    const mapping = FILTER_FIELD_MAP[f];
    return mapping?.field === 'tags';
  });

  return (
    <ExploreFilterContext.Provider
      value={{
        selectedFilters,
        toggleFilter,
        clearFilters,
        setFilters,
        isFilterSelected,
        genderFilter,
        styleFilter,
        bodyTypeFilter,
        ageRangeFilter,
        tagFilters,
      }}
    >
      {children}
    </ExploreFilterContext.Provider>
  );
}

export function useExploreFilters() {
  const context = useContext(ExploreFilterContext);
  if (context === undefined) {
    throw new Error(
      'useExploreFilters must be used within an ExploreFilterProvider',
    );
  }
  return context;
}

/**
 * Optional hook that returns null if context is not available
 * Useful for components that may be used outside the provider
 */
export function useExploreFiltersOptional() {
  return useContext(ExploreFilterContext);
}
